import { useMemo, useState, type ReactNode } from 'react';
import { T, useGT, Var } from 'gt-react';
import { format } from 'date-fns';
import {
  BuildingsIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  FileTextIcon,
  HashIcon,
  InfoIcon,
  ReceiptIcon,
  UserIcon,
  WalletIcon,
} from '@phosphor-icons/react';
import type { APInvoice, SearchDocumentResult } from '@erp/rpc';
import {
  APInvoiceStatus,
  EntityAssociationType,
  ProductModule,
  ReferenceEntityType,
  SystemEntity,
  apPaymentTermsPriority,
} from '@erp/shared-types';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { FieldLabel } from '@/components/common/FieldLabel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PillList } from '@/components/common/pills/PillList';
import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';
import { DocumentAutocomplete } from '@/components/accounts-payable/DocumentAutocomplete';
import { PaymentStatusBadge } from '@/components/accounts-payable/shared/payment-status-badge';
import { SystemEntityInline } from '@/components/system-entities';
import { ExternalReferencesList } from '@/components/system-entities/external-references-list';
import {
  useCreateEntityAssociation,
  useEntityAssociationsList,
} from '@/api/rpc/hooks/core/useEntityAssociations';
import { useUpdateAPInvoice } from '@/api/rpc/hooks/accounts-payable/useUpdateAPInvoice';
import { useUsersByIds } from '@/api/core/users/useUsersByIds';
import { useGetModuleConfig } from '@/api/org/config/modules';
import { formatDateOnlyString, formatDatetime } from '@/utils/dateUtils';
import { cn } from '@/lib/utils';
import {
  DOCUMENT_TYPE_TO_SYSTEM_ENTITY,
  acceptedLabel,
  dateOnlyValue,
  formatInvoiceTotal,
  invoiceNumberToSave,
  invoiceNumberUnchanged,
  invoicePaymentTerms,
  linkedReferenceEntities,
  scheduledDayChanged,
  toDateOnly,
} from './invoiceFieldValues';

const EMPTY_EXTRACTED_TOTAL_NOTES: string[] = [];

type UpdateInvoice = ReturnType<typeof useUpdateAPInvoice>;

interface GroupProps {
  invoice: APInvoice;
  isEditable: boolean;
  updateInvoice: UpdateInvoice;
  /** Non-blocking notes when the line total differs from the extracted total. */
  extractedTotalNotes?: string[];
}

// ── Primitives ───────────────────────────────────────────────────────────────

function FieldGroup({
  heading,
  className,
  children,
}: {
  heading: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn('space-y-2', className)}>
      <h4 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {heading}
      </h4>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </section>
  );
}

function ReadOnlyValue({
  value,
  placeholder,
}: {
  value: string | null | undefined;
  placeholder: string;
}) {
  return value ? (
    <div className="text-sm font-medium">{value}</div>
  ) : (
    <div className="text-sm text-muted-foreground">{placeholder}</div>
  );
}

/** A labelled date: a picker while the invoice is editable, text otherwise. */
function DateField({
  label,
  tooltip,
  trailing,
  value,
  isEditable,
  onSelect,
  pickerPlaceholder,
  emptyLabel,
  hint,
}: {
  label: ReactNode;
  tooltip: string;
  trailing?: ReactNode;
  value: Date | undefined;
  isEditable: boolean;
  onSelect: (date: Date | undefined) => void;
  pickerPlaceholder: string;
  emptyLabel: string;
  /** Secondary line under the value. */
  hint?: ReactNode;
}) {
  return (
    <div className="space-y-0.5">
      <FieldLabel icon={CalendarIcon} tooltip={tooltip} trailing={trailing}>
        {label}
      </FieldLabel>
      {isEditable ? (
        <DatePicker
          value={value}
          onSelect={onSelect}
          placeholder={pickerPlaceholder}
        />
      ) : (
        <ReadOnlyValue
          value={
            value ? formatDateOnlyString(format(value, 'yyyy-MM-dd')) : null
          }
          placeholder={emptyLabel}
        />
      )}
      {hint}
    </div>
  );
}

/**
 * When the invoice reached us, shown under the invoice date so the two
 * dates read together. Carries the definition on hover like a field label.
 */
function ReceivedHint({ receivedAt }: { receivedAt: string }) {
  const gt = useGT();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <p className="w-fit cursor-help text-xs text-muted-foreground">
          <T context="invoice slide-in: line under Invoice Date giving when the supplier invoice arrived, usually email inbox time">
            Received <Var>{formatDatetime(receivedAt)}</Var>
          </T>
        </p>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">
        {gt(
          'When the invoice reached us — the time the email landed in the inbox, or when the document was uploaded.',
          { context: 'tooltip on the Received line of an AP invoice' },
        )}
      </TooltipContent>
    </Tooltip>
  );
}

// ── Invoice group cells ──────────────────────────────────────────────────────

/**
 * The supplier's invoice number. Editable on a draft or matched invoice.
 * The number is an identifier, so it is stored only when Save is pressed.
 */
function InvoiceNumberField({
  invoice,
  isEditable,
  updateInvoice,
}: GroupProps) {
  const gt = useGT();
  const [draftState, setDraftState] = useState<{
    invoiceId: string;
    value: string;
  } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const draft = draftState?.invoiceId === invoice.id ? draftState.value : null;
  const saved = invoice.invoiceNumber ?? '';
  const displayed = draft ?? saved;
  const dirty = !invoiceNumberUnchanged(
    invoiceNumberToSave(displayed),
    invoice.invoiceNumber,
  );

  async function save() {
    const attempted = displayed;
    const invoiceNumber = invoiceNumberToSave(attempted);
    if (
      saving ||
      invoiceNumberUnchanged(invoiceNumber, invoice.invoiceNumber)
    ) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await updateInvoice.mutateAsync({ id: invoice.id, invoiceNumber });
      setDraftState((current) =>
        current?.invoiceId === invoice.id && current.value === attempted
          ? null
          : current,
      );
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      setSaveError(
        message ||
          gt('Could not save the invoice number', {
            context:
              'error under the Invoice Number field when saving the number fails',
          }),
      );
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setDraftState(null);
    setSaveError(null);
  }

  return (
    <div className="space-y-0.5">
      <FieldLabel
        icon={HashIcon}
        tooltip={gt(
          "The number printed on the supplier's invoice. Correct it when extraction read it wrong, then choose Save. Leaving the field does not save.",
          { context: 'tooltip on the Invoice Number field of an AP invoice' },
        )}
      >
        <T context="invoice slide-in: the supplier's invoice number">
          Invoice Number
        </T>
      </FieldLabel>
      {isEditable ? (
        <>
          <Input
            value={displayed}
            onChange={(event) => {
              setSaveError(null);
              setDraftState({
                invoiceId: invoice.id,
                value: event.target.value,
              });
            }}
            onBlur={() => {
              if (
                invoiceNumberUnchanged(
                  invoiceNumberToSave(displayed),
                  invoice.invoiceNumber,
                )
              ) {
                cancel();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && dirty) {
                event.preventDefault();
                void save();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                cancel();
              }
            }}
            placeholder={gt('Enter invoice number', {
              context:
                'placeholder for the editable invoice number on a draft or matched AP invoice',
            })}
            aria-label={gt('Invoice number', {
              context: 'accessible name for the editable AP invoice number',
            })}
            aria-invalid={saveError != null}
            maxLength={255}
          />
          {dirty ? (
            <div className="flex items-center gap-1.5 pt-1">
              <Button
                type="button"
                size="sm"
                disabled={saving}
                onClick={() => void save()}
              >
                <T context="button that stores an edited AP invoice number">
                  Save
                </T>
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={saving}
                onClick={cancel}
              >
                <T context="button that discards an unsaved AP invoice number edit">
                  Cancel
                </T>
              </Button>
            </div>
          ) : null}
          {saveError ? (
            <p className="text-xs text-destructive" role="alert">
              {saveError}
            </p>
          ) : null}
        </>
      ) : (
        <ReadOnlyValue
          value={invoice.invoiceNumber}
          placeholder={gt('Not specified')}
        />
      )}
    </div>
  );
}

function SupplierField({ invoice, isEditable, updateInvoice }: GroupProps) {
  const gt = useGT();
  const supplierId = invoice.supplierId || undefined;
  return (
    <div className="space-y-0.5">
      <FieldLabel
        icon={BuildingsIcon}
        tooltip={gt(
          'The vendor billing you. Pick a different supplier if extraction read it wrong — matching and payment details follow the supplier.',
          { context: 'tooltip on the Supplier field of an AP invoice' },
        )}
      >
        <T>Supplier</T>
      </FieldLabel>
      {isEditable ? (
        <SupplierAutocomplete
          supplierId={supplierId}
          onSelect={(next) =>
            updateInvoice.mutate({ id: invoice.id, supplierId: next || null })
          }
          className="w-full"
        />
      ) : supplierId ? (
        <SystemEntityInline
          entityType={SystemEntity.AP_SUPPLIER}
          entityId={supplierId}
        />
      ) : (
        <div className="text-xs text-muted-foreground">-</div>
      )}
    </div>
  );
}

function TotalAmountField({
  invoice,
  extractedTotalNotes = EMPTY_EXTRACTED_TOTAL_NOTES,
}: {
  invoice: APInvoice;
  extractedTotalNotes?: string[];
}) {
  const gt = useGT();
  return (
    <div className="space-y-0.5">
      <FieldLabel
        icon={CurrencyDollarIcon}
        tooltip={gt(
          'The invoice total including tax, as read from the document. Line items below should add up to it.',
          { context: 'tooltip on the Total Amount field of an AP invoice' },
        )}
      >
        <T>Total Amount</T>
      </FieldLabel>
      <div className="text-lg font-semibold tabular-nums">
        {formatInvoiceTotal(invoice.totalAmount, invoice.currency)}
      </div>
      {extractedTotalNotes.map((note) => (
        <p key={note} className="text-xs text-amber-700">
          {note}
        </p>
      ))}
    </div>
  );
}

/**
 * Reference numbers plus, while editable, the search that links a PO / SO /
 * shipment. The two live together because linking is how a reference stops
 * being "read off the document" and becomes a record we match against.
 */
function ReferenceIdsField({ invoice, isEditable }: GroupProps) {
  const gt = useGT();
  const associationsQuery = useEntityAssociationsList({
    entityId: invoice.id,
    entityType: SystemEntity.AP_INVOICE,
    associationType: EntityAssociationType.AP_INVOICE_MATCHING,
  });
  const linkedRefEntities = useMemo(
    () => linkedReferenceEntities(associationsQuery.data?.associations ?? []),
    [associationsQuery.data?.associations],
  );
  const associatedDocumentIds = useMemo(
    () => linkedRefEntities.map((entity) => entity.entityId),
    [linkedRefEntities],
  );
  const nonLinkedRefEntities = useMemo(
    () => [{ entityType: ReferenceEntityType.Invoice, entityId: invoice.id }],
    [invoice.id],
  );
  const createAssociation = useCreateEntityAssociation();

  function linkDocument(document: SearchDocumentResult) {
    if (createAssociation.isPending) return;
    void createAssociation.mutateAsync({
      sourceEntityType: SystemEntity.AP_INVOICE,
      sourceEntityId: invoice.id,
      targetEntityType: DOCUMENT_TYPE_TO_SYSTEM_ENTITY[document.documentType],
      targetEntityId: document.id,
      associationType: EntityAssociationType.AP_INVOICE_MATCHING,
    });
  }

  const extractedRefs = invoice.referenceIds ?? [];

  return (
    <div className="col-span-2 space-y-1">
      <FieldLabel
        icon={FileTextIcon}
        tooltip={gt(
          'Order, shipment and document numbers read from the invoice or linked to it. Linked references open their record; non-linked ones were only read off the document. Use the search to link the PO, sales order or shipment this invoice bills against — matching runs against linked documents.',
          { context: 'tooltip on the Reference IDs field of an AP invoice' },
        )}
      >
        <T>Reference IDs</T>
      </FieldLabel>
      <ExternalReferencesList
        entities={linkedRefEntities}
        nonLinkedEntities={nonLinkedRefEntities}
        collapsedLimit={Number.POSITIVE_INFINITY}
        fallback={
          extractedRefs.length > 0 ? (
            <PillList values={extractedRefs} />
          ) : (
            <div className="text-sm text-muted-foreground">—</div>
          )
        }
      />
      {isEditable && (
        <DocumentAutocomplete
          invoiceId={invoice.id}
          supplierId={invoice.supplierId || undefined}
          invoiceDate={invoice.invoiceDate}
          invoiceAmount={invoice.totalAmount}
          currency={invoice.currency || 'USD'}
          excludeDocumentIds={associatedDocumentIds}
          onSelect={linkDocument}
          placeholder={gt('Link a PO, sales order or shipment…', {
            context:
              'placeholder for the search that links a document to an AP invoice',
          })}
        />
      )}
    </div>
  );
}

// ── Groups ───────────────────────────────────────────────────────────────────

/**
 * What the supplier sent us: which invoice and how much on the first row,
 * who and when on the second, then the orders it bills against.
 */
function InvoiceFactsFields(props: GroupProps) {
  const gt = useGT();
  const { invoice, isEditable, updateInvoice, extractedTotalNotes } = props;
  return (
    <FieldGroup
      heading={
        <T context="invoice slide-in: heading over the fields describing the supplier's bill">
          Invoice
        </T>
      }
    >
      <InvoiceNumberField key={invoice.id} {...props} />
      <TotalAmountField
        invoice={invoice}
        extractedTotalNotes={extractedTotalNotes}
      />
      <SupplierField {...props} />
      <DateField
        label={<T>Invoice Date</T>}
        tooltip={gt(
          "The date printed on the supplier's invoice. Payment terms count from this date.",
          { context: 'tooltip on the Invoice Date field of an AP invoice' },
        )}
        value={dateOnlyValue(invoice.invoiceDate)}
        isEditable={isEditable}
        onSelect={(date) => {
          // Invoice date is required; ignore a clear.
          const invoiceDate = toDateOnly(date);
          if (invoiceDate)
            updateInvoice.mutate({ id: invoice.id, invoiceDate });
        }}
        pickerPlaceholder={gt('Select date')}
        emptyLabel={gt('Not specified')}
        hint={
          invoice.receivedAt ? (
            <ReceivedHint receivedAt={invoice.receivedAt} />
          ) : undefined
        }
      />
      <ReferenceIdsField {...props} />
    </FieldGroup>
  );
}

function ScheduledDateExplanation({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help">
          <InfoIcon className="h-3 w-3 text-muted-foreground" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm">
        <p className="font-medium mb-1">
          <T>Calculation Details</T>
        </p>
        <pre className="whitespace-pre-wrap text-xs">{text}</pre>
      </TooltipContent>
    </Tooltip>
  );
}

/** Printed terms, else the supplier's ERP terms, with where they came from. */
function PaymentTermsField({ invoice }: { invoice: APInvoice }) {
  const gt = useGT();
  const { data: apModuleConfig } = useGetModuleConfig(
    ProductModule.AP_INVOICING,
  );
  const paymentTerms = invoicePaymentTerms(
    invoice.paymentTerms,
    invoice.supplier?.paymentTerms,
    apPaymentTermsPriority(apModuleConfig?.config?.ap),
  );

  return (
    <div className="space-y-0.5">
      <FieldLabel
        icon={ReceiptIcon}
        tooltip={gt(
          "Terms printed on the invoice, e.g. Net 30 or 2/10 Net 30, or the supplier's terms from the ERP. Which one applies follows the payment terms priority in AP settings. With any due date printed on the invoice, they drive the due date and the scheduled pay date.",
          { context: 'tooltip on the Payment Terms field of an AP invoice' },
        )}
      >
        <T>Payment Terms</T>
      </FieldLabel>
      <ReadOnlyValue value={paymentTerms.terms} placeholder="—" />
      {paymentTerms.source === 'company' && (
        <div className="text-xs text-muted-foreground">
          <T context="AP invoice: the payment terms priority puts the organization's default terms first, so they set the due date">
            Company default terms apply
          </T>
        </div>
      )}
      {paymentTerms.source === 'supplier' && (
        <div className="text-xs text-muted-foreground">
          <T context="AP invoice: the payment terms shown come from the supplier record, not the invoice document">
            Supplier terms from the ERP
          </T>
        </div>
      )}
      {paymentTerms.other?.source === 'supplier' && (
        <div className="text-xs text-muted-foreground">
          <T context="AP invoice: the supplier's ERP payment terms, shown under the terms printed on the invoice">
            Supplier terms: <Var>{paymentTerms.other.terms}</Var>
          </T>
        </div>
      )}
      {paymentTerms.other?.source === 'invoice' && (
        <div className="text-xs text-muted-foreground">
          <T context="AP invoice: the terms printed on the invoice document, shown under the supplier's ERP terms that take priority">
            Printed on invoice: <Var>{paymentTerms.other.terms}</Var>
          </T>
        </div>
      )}
    </div>
  );
}

/** How and when we pay: terms → due → preferred → scheduled → accepted → paid. */
function PaymentFields({ invoice, isEditable, updateInvoice }: GroupProps) {
  const gt = useGT();

  const approvedByUserId = invoice.approvedByUserId ?? null;
  const approvedByUsersQuery = useUsersByIds(
    approvedByUserId ? [approvedByUserId] : [],
    Boolean(approvedByUserId),
  );
  const approverName = approvedByUserId
    ? (approvedByUsersQuery.data?.[0]?.name ??
      `User ${approvedByUserId.slice(0, 8)}...`)
    : null;

  function handleScheduledPaymentDateChange(date: Date | undefined) {
    if (!scheduledDayChanged(date, invoice.scheduledPaymentDate)) return;
    updateInvoice.mutate({
      id: invoice.id,
      scheduledPaymentDate: date ? date.toISOString() : null,
      scheduledPaymentExplanation: date ? 'Manually set by user' : null,
    });
  }

  return (
    <FieldGroup
      className="border-t pt-3"
      heading={
        <T context="invoice slide-in: heading over the fields describing how and when the invoice gets paid">
          Payment
        </T>
      }
    >
      <PaymentTermsField invoice={invoice} />

      <DateField
        label={<T>Due Date</T>}
        tooltip={gt(
          "The date payment is due under the supplier's terms. Paying after it risks late fees or lost discounts.",
          { context: 'tooltip on the Due Date field of an AP invoice' },
        )}
        value={dateOnlyValue(invoice.dueDate)}
        isEditable={isEditable}
        onSelect={(date) =>
          updateInvoice.mutate({ id: invoice.id, dueDate: toDateOnly(date) })
        }
        pickerPlaceholder={gt('Select due date')}
        emptyLabel={gt('Not specified')}
      />

      <DateField
        label={<T>Preferred Pay Date</T>}
        tooltip={gt(
          'A date you would rather pay on — to capture an early-payment discount or line up with a cash cycle. When set, it overrides the terms-based schedule.',
          {
            context: 'tooltip on the Preferred Pay Date field of an AP invoice',
          },
        )}
        value={dateOnlyValue(invoice.preferredPayDate)}
        isEditable={isEditable}
        onSelect={(date) =>
          updateInvoice.mutate({
            id: invoice.id,
            preferredPayDate: toDateOnly(date),
          })
        }
        pickerPlaceholder={gt('Select preferred date')}
        emptyLabel={gt('Not specified')}
      />

      <DateField
        label={<T>Scheduled Pay Date</T>}
        tooltip={gt(
          'The date the payment is planned to go out. Calculated from the terms and invoice date unless someone sets it by hand; the payment run picks invoices up by this date.',
          {
            context: 'tooltip on the Scheduled Pay Date field of an AP invoice',
          },
        )}
        trailing={
          invoice.scheduledPaymentExplanation ? (
            <ScheduledDateExplanation
              text={invoice.scheduledPaymentExplanation}
            />
          ) : null
        }
        // scheduledPaymentDate is a full ISO timestamp, not DATEONLY
        value={
          invoice.scheduledPaymentDate
            ? new Date(invoice.scheduledPaymentDate)
            : undefined
        }
        isEditable={isEditable}
        onSelect={handleScheduledPaymentDateChange}
        pickerPlaceholder={gt('Select scheduled date')}
        emptyLabel={gt('Not scheduled')}
      />

      <div className="space-y-0.5">
        <FieldLabel
          icon={UserIcon}
          tooltip={gt(
            'Who accepted the line-item matches, and when. Accepting confirms the invoice bills what we ordered and received, and moves it on to payment scheduling.',
            { context: 'tooltip on the Accepted field of an AP invoice' },
          )}
        >
          <T>Accepted</T>
        </FieldLabel>
        <ReadOnlyValue
          value={acceptedLabel({
            approvedAt: invoice.approvedAt,
            approverName,
          })}
          placeholder={gt('Not accepted')}
        />
      </div>

      <div className="space-y-0.5">
        <FieldLabel
          icon={WalletIcon}
          tooltip={gt(
            'Whether money has gone out against this invoice. Paid means the full amount was settled; partially paid means some of it has; overdue means a balance is still open past the due date.',
            { context: 'tooltip on the Payment Status field of an AP invoice' },
          )}
        >
          <T context="invoice slide-in: field showing whether the invoice has been paid">
            Payment Status
          </T>
        </FieldLabel>
        <div className="flex items-center gap-2 text-sm">
          <PaymentStatusBadge
            status={invoice.paymentStatus}
            dueDate={invoice.dueDate}
          />
          {invoice.paidAt && (
            <span className="text-muted-foreground">
              {formatDateOnlyString(invoice.paidAt)}
            </span>
          )}
        </div>
      </div>
    </FieldGroup>
  );
}

/**
 * The invoice's editable facts, in two groups: what the supplier sent us
 * (Invoice) and how and when we pay it (Payment). This is how an AP analyst
 * reads a bill — identity first, then the money timeline.
 */
export function InvoiceFields({
  invoice,
  extractedTotalNotes,
}: {
  invoice: APInvoice;
  extractedTotalNotes?: string[];
}) {
  const updateInvoice = useUpdateAPInvoice();
  // Allow editing for DRAFT and MATCHED invoices
  const isEditable =
    invoice.status === APInvoiceStatus.DRAFT ||
    invoice.status === APInvoiceStatus.MATCHED;
  const props = {
    invoice,
    isEditable,
    updateInvoice,
    extractedTotalNotes,
  };

  return (
    <div className="space-y-4">
      <InvoiceFactsFields {...props} />
      <PaymentFields {...props} />
    </div>
  );
}
